// TimesheetDetailModal.js
import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { FiTrash } from 'react-icons/fi';
import { apiDelete } from 'utils/apiFetch';
import pathObj from 'utils/pathObj';
import helpers from 'utils/helpers';
import useToastContext from 'hooks/useToastContext';

const TimesheetDetailModal = ({ show, onHide, timesheet, getAttendanceIcon, formatTime }) => {
  const { t } = useTranslation();
  const notification = useToastContext();

  if (!timesheet || !show) return null;
  
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // ═══ Get leave badge for detail view ═══
  const getLeaveBadge = (entry) => {
    if (!entry?.isOnLeave) return null;
    
    if (entry.leaveType === 'paid_leave_of_absence') {
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
          Paid Leave of Absence
        </span>
      );
    }
    if (entry.leaveType === 'unpaid_leave_of_absence') {
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
          Unpaid Leave of Absence
        </span>
      );
    }
    if (entry.leaveType === 'paid_leave') {
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
          Paid Leave
        </span>
      );
    }
    if (entry.leaveType === 'unpaid_leave') {
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
          Unpaid Leave
        </span>
      );
    }
    return null;
  };

  // ═══ Get attendance text for detail view ═══
  const getAttendanceText = (entry) => {
    if (!entry) return '-';
    
    // If on leave, show the leave badge (not text)
    if (entry.isOnLeave) {
      return getLeaveBadge(entry) || 'On Leave';
    }
    
    if (entry.isAbsent) {
      return <span className="text-red-600 font-medium">Absent</span>;
    }
    
    if (entry.isLate && entry.isEarly) {
      return <span className="text-yellow-600 font-medium">Late & Early</span>;
    }
    if (entry.isLate) {
      return <span className="text-yellow-600 font-medium">Late</span>;
    }
    if (entry.isEarly) {
      return <span className="text-blue-600 font-medium">Early</span>;
    }
    
    return <span className="text-green-600 font-medium">Present</span>;
  };

  // ═══ Get break display ═══
  const getBreakDisplay = (entry) => {
    if (!entry) return '0 breaks';
    if (entry.breaks?.length > 0) {
      return (
        <div>
          <div>{entry.breaks.length} breaks</div>
          {entry.breaks.map((b, bi) => (
            <div key={bi} className="text-xs text-gray-500">
              {b.startBreakTime && dayjs(b.startBreakTime).format('HH:mm')}
              {b.endBreakTime && ` - ${dayjs(b.endBreakTime).format('HH:mm')}`}
            </div>
          ))}
        </div>
      );
    }
    return '0 breaks';
  };

  // ═══ Get regular hours display with paid/unpaid indicator ═══
  const getRegularHoursDisplay = (entry) => {
    if (!entry) return '0h';
    
    const hours = entry.regularHours || 0;
    
    if (entry.isOnLeave) {
      return (
        <div>
          <div>{hours}h</div>
          {/* <div className="text-[10px]">
            {entry.isPaidLeave ? (
              <span className="text-green-600">✓ Paid</span>
            ) : (
              <span className="text-orange-600">✗ Unpaid</span>
            )}
          </div> */}
        </div>
      );
    }
    
    return `${hours}h`;
  };

  const handleDeleteAdjustment = (adjustmentId) => {
    helpers.alertFunction(
      'Are you sure you want to delete this adjustment?',
      { timesheetId: timesheet._id, adjustmentId },
      async (data) => {
        try {
          const res = await apiDelete(
            `${pathObj.deleteAdjustment}/${data.timesheetId}/adjustment/${data.adjustmentId}`
          );
          if (res?.data?.success) {
            notification.success('Adjustment deleted');
            onHide();
          } else {
            notification.error(res?.data?.message);
          }
        } catch (error) {
          notification.error('Something went wrong');
        }
      },
      true
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black opacity-40" onClick={onHide} />
      
      <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden outline-none focus:outline-none pointer-events-none">
        <div className="relative w-full my-6 mx-auto max-w-4xl pointer-events-auto">
          <div className="border-0 rounded-xl shadow-xl relative flex flex-col w-full bg-white outline-none max-h-[90vh]">
            
            {/* Header */}
            <div className="p-4 flex items-center justify-between border-b border-solid border-slate-100 rounded-t">
              <h3 className="font-bold text-lg text-gray-800">
                {t('TIMESHEET_DETAILS')} - {timesheet.employeeId?.name}
              </h3>
              <button 
                type="button" 
                onClick={onHide} 
                className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl font-light">×</span>
              </button>
            </div>

            {/* Body */}
            <div className="relative p-5 flex-auto max-h-[70vh] overflow-y-auto">
              
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <h5 className="text-blue-600 text-lg font-semibold mb-0">{timesheet.totalRegularHours}h</h5>
                  <small className="text-gray-600">{t('REGULAR_HRS')}</small>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center">
                  <h5 className="text-yellow-600 text-lg font-semibold mb-0">{timesheet.totalOvertimeHours}h</h5>
                  <small className="text-gray-600">{t('OVERTIME_HRS')}</small>
                </div>
                <div className="bg-cyan-50 rounded-lg p-3 text-center">
                  <h5 className="text-cyan-600 text-lg font-semibold mb-0">{timesheet.totalBreakHours || 0}h</h5>
                  <small className="text-gray-600">{t('BREAK_HRS')}</small>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    timesheet.status === 'approved' ? 'bg-green-100 text-green-800' : 
                    timesheet.status === 'rejected' ? 'bg-red-100 text-red-800' : 
                    timesheet.status === 'submitted' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {timesheet.status}
                  </span>
                  <br />
                  <small className="text-gray-600">{t('STATUS')}</small>
                </div>
              </div>

              {/* ═══ Leave Summary - Only show once at the top ═══ */}
              {(timesheet.totalPaidLeaveDays > 0 || timesheet.totalUnpaidLeaveDays > 0) && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex gap-3 flex-wrap">
                    {timesheet.totalPaidLeaveDays > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-100 text-green-800 rounded-full">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Paid Leave of Absence: {timesheet.totalPaidLeaveDays} day{timesheet.totalPaidLeaveDays > 1 ? 's' : ''}
                      </span>
                    )}
                    {timesheet.totalUnpaidLeaveDays > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-orange-100 text-orange-800 rounded-full">
                        <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                        Unpaid Leave of Absence: {timesheet.totalUnpaidLeaveDays} day{timesheet.totalUnpaidLeaveDays > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Daily Breakdown */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">{t('DAY')}</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">{t('DATE')}</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">{t('PUNCH_IN')}</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">{t('PUNCH_OUT')}</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">{t('BREAKS')}</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">{t('REGULAR')}</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">{t('OVERTIME')}</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">{t('ATTENDANCE')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {daysOfWeek.map((dayName, idx) => {
                      const weekStart = dayjs(timesheet.weekStartDate);
                      const targetDate = weekStart.add(idx, 'day').format('YYYY-MM-DD');
                      const entry = timesheet.dailyEntries?.find(e => 
                        dayjs(e.date).format('YYYY-MM-DD') === targetDate
                      );
                      
                      // ═══ Determine if this day has any data ═══
                      const hasData = entry && (entry.punchInDateTime || entry.punchOutDateTime || entry.isOnLeave || entry.isAbsent);
                      
                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-start">{dayName}</td>
                          <td className="px-3 py-2 text-center">{entry ? dayjs(entry.date).format('MMM D') : '-'}</td>
                          <td className="px-3 py-2 text-center">
                            {entry?.punchInDateTime 
                              ? dayjs(entry.punchInDateTime).format('hh:mm A') 
                              : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {entry?.punchOutDateTime 
                              ? dayjs(entry.punchOutDateTime).format('hh:mm A') 
                              : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {getBreakDisplay(entry)}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {getRegularHoursDisplay(entry)}
                          </td>
                          <td className="px-3 py-2 text-center">{entry?.overtimeHours || 0}h</td>
                          <td className="px-3 py-2 text-center">
                            {hasData ? getAttendanceText(entry) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Adjustments History */}
              {timesheet.adjustments?.length > 0 && (
                <div className="mt-4">
                  <h6 className="font-semibold text-gray-700 mb-2">{t('ADJUSTMENT_HISTORY')}</h6>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">{t('DATE')}</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">{t('FIELD')}</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">{t('OLD_VALUE')}</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">{t('NEW_VALUE')}</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">{t('REASON')}</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">{t('ACTIONS')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {timesheet.adjustments.map((adj, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2">{dayjs(adj.createdAt).format('MMM D, hh:mm A')}</td>
                            <td className="px-3 py-2">{adj.field === 'punchInDateTime' ? 'Punch In' : 'Punch Out'}</td>
                            <td className="px-3 py-2">{adj.oldValue}</td>
                            <td className="px-3 py-2">{dayjs(adj.newValue).format('MMM D, hh:mm A')}</td>
                            <td className="px-3 py-2">{adj.reason}</td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => handleDeleteAdjustment(adj._id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <FiTrash className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>  
          </div>
        </div>
      </div>
    </>
  );
};

export default TimesheetDetailModal;